import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { AnalyticsEventsService } from '@techspert-io/analytics';
import { CognitoAuthService } from '@techspert-io/auth';
import {
  IOpportunity,
  IOpportunitySegmentBaseStats,
  IOpportunitySegmentStats,
} from '@techspert-io/opportunities';
import { EditProjectKeywordsDialogComponent } from '@techspert-io/project-keywords';
import { tap } from 'rxjs/operators';
import type { Cell, Sheet, SheetData } from 'write-excel-file/browser';
import { FileStoreService } from '../../../shared/services/filestore.service';
import { ExpertStoreService } from '../../../shared/state/expert.service';
import { WorksheetService } from '../services/worksheet.service';

const WORKBOOK_COLORS = {
  totalRowBackground: '#565252',
  emphasizedEvenRowBackground: '#E1E1E1',
  evenRowBackground: '#F5F5F5',
  emphasizedOddRowBackground: '#EBEBEB',
  defaultBackground: '#FFFFFF',
  totalRowText: '#FFFFFF',
  cellBorder: '#D8CCCC',
  headerBorder: '#000000',
} as const;

interface IDataSource extends IOpportunitySegmentStats {
  transcriptionSummaryKey?: string;
}

@Component({
  selector: 'app-geographic-progress',
  templateUrl: './geographic-progress.component.html',
  styleUrls: ['./geographic-progress.component.scss'],
  standalone: false,
})
export class GeographicProgressComponent implements OnInit, AfterViewInit {
  @Input() opportunity: IOpportunity;
  @Output() adjustTabIndex = new EventEmitter();
  @Output() openOmnisearch = new EventEmitter();

  public displayedColumns = [
    'summary',
    'geography',
    'segment',
    'screening',
    'inPortal',
    'scheduled',
    'completed',
    'confirmed',
    'target',
  ];
  public dataSource: MatTableDataSource<IDataSource>;
  public emptyExperts: boolean = false;

  get showOmnisearchBanner(): boolean {
    return (
      this.cognitoAuthService.loggedInUser &&
      this.cognitoAuthService.loggedInUser.clients[this.opportunity.clientId] &&
      this.cognitoAuthService.loggedInUser.clients[this.opportunity.clientId]
        .features.omnisearchEnabled
    );
  }

  constructor(
    public expertStore: ExpertStoreService,
    private cognitoAuthService: CognitoAuthService,
    private worksheetService: WorksheetService,
    private fileStore: FileStoreService,
    private analyticsEventsService: AnalyticsEventsService,
    private dialog: MatDialog
  ) {}

  @ViewChild(MatSort) sort: MatSort;

  ngOnInit(): void {
    this.dataSource = new MatTableDataSource(
      this.opportunity.opportunitySegmentStats
        .map((s) => ({
          ...s,
          transcriptionSummaryKey: this.opportunity.opportunitySegments.find(
            (oS) => oS.opportunitySegmentId === s.opportunitySegmentId
          )?.transcriptionSummaryKey,
        }))
        .sort(
          (a, b) =>
            a.geography
              .toLowerCase()
              .localeCompare(b.geography.toLowerCase()) ||
            a.segment.toLowerCase().localeCompare(b.segment.toLowerCase())
        )
    );
    this.emptyExperts = this.isEmpty(
      this.opportunity.opportunitySegmentStatsTotal
    );
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
  }

  getTotal(phase: string): number {
    return this.dataSource
      ? this.dataSource.data.reduce((curr, acc) => (curr += acc[phase]), 0)
      : 0;
  }

  adjustTab(val: number): void {
    this.adjustTabIndex.emit(val);
  }

  downloadSummary(segment: IDataSource): void {
    if (!segment.transcriptionSummaryKey) {
      return;
    }

    const fileName = `[ECHO] - Summary_${segment.geography}_${segment.segment}.txt`;

    this.fileStore
      .downloadExpertFile(segment.transcriptionSummaryKey, fileName)
      .pipe(
        tap(() =>
          this.analyticsEventsService.sendEvent('download', {
            event_category: 'segment_transcript_summary',
            event_label: `${segment.geography}_${segment.segment}`,
            value: segment.opportunitySegmentId,
          })
        )
      )
      .subscribe();
  }

  async downloadProgressCsv(): Promise<void> {
    const formattedOpportunityName = this.opportunity.opportunityName
      .replace(/[?:\\\/[\]*]/g, '')
      .trim()
      .slice(0, 31);

    await this.worksheetService.save(
      [this.formatWorkbook(formattedOpportunityName)],
      'export'
    );
  }

  manageKeywords() {
    this.dialog
      .open(EditProjectKeywordsDialogComponent, {
        width: '600px',
        autoFocus: false,
        data: { opportunityId: this.opportunity.opportunityId },
      })
      .afterClosed()
      .subscribe();
  }

  private isEmpty(total: IOpportunitySegmentBaseStats): boolean {
    return (
      !total ||
      (total.completed === 0 &&
        total.confirmed === 0 &&
        total.scheduled === 0 &&
        total.inPortal === 0 &&
        total.screening === 0)
    );
  }

  private formatWorkbook(sheetName: string): Sheet<Blob> {
    const headers = [
      'Geography',
      'Profile Type',
      'Screening',
      'New Profiles',
      'Upcoming Connections',
      'Completed Connections',
      'Confirmed Connections',
      'Target',
    ];
    const totals = this.dataSource.data.reduce(
      (result, row) => ({
        screening: result.screening + row.screening,
        inPortal: result.inPortal + row.inPortal,
        scheduled: result.scheduled + row.scheduled,
        completed: result.completed + row.completed,
        confirmed: result.confirmed + row.confirmed,
        target: result.target + row.target,
      }),
      {
        screening: 0,
        inPortal: 0,
        scheduled: 0,
        completed: 0,
        confirmed: 0,
        target: 0,
      }
    );
    const rows: Array<Array<string | number>> = [
      headers,
      ...this.dataSource.data.map((row) => [
        row.geography,
        row.segment,
        row.screening,
        row.inPortal,
        row.scheduled,
        row.completed,
        row.confirmed,
        row.target,
      ]),
      [
        'PROJECT TOTALS',
        '',
        totals.screening,
        totals.inPortal,
        totals.scheduled,
        totals.completed,
        totals.confirmed,
        totals.target,
      ],
    ];

    const data: SheetData = rows.map((row, rowIndex) =>
      row.map((value, columnIndex): Cell => {
        const rowNumber = rowIndex + 1;
        const columnNumber = columnIndex + 1;
        const isHeader = rowNumber === 1;
        const isTotalRow = rowNumber === rows.length;
        const isEmphasized = columnNumber >= headers.length - 1;
        const isEvenRow = rowNumber % 2 === 0;

        return {
          value,
          fontFamily: 'Arial',
          fontSize: 10,
          fontWeight:
            isHeader || isTotalRow || isEmphasized ? 'bold' : undefined,
          textColor: isTotalRow ? WORKBOOK_COLORS.totalRowText : undefined,
          alignVertical: 'center',
          backgroundColor: this.getCellBackgroundColor(
            isTotalRow,
            isEvenRow,
            isEmphasized,
            isHeader
          ),
          leftBorderColor: WORKBOOK_COLORS.cellBorder,
          leftBorderStyle: 'thin',
          topBorderColor: WORKBOOK_COLORS.cellBorder,
          topBorderStyle: 'thin',
          rightBorderColor: WORKBOOK_COLORS.cellBorder,
          rightBorderStyle: 'thin',
          bottomBorderColor: isHeader
            ? WORKBOOK_COLORS.headerBorder
            : WORKBOOK_COLORS.cellBorder,
          bottomBorderStyle: isHeader ? 'double' : 'thin',
        };
      })
    );

    return {
      sheet: sheetName,
      data,
      columns: headers.map(() => ({ width: 23 })),
    };
  }

  private getCellBackgroundColor(
    isTotalRow: boolean,
    isEvenRow: boolean,
    isEmphasized: boolean,
    isHeader: boolean
  ): string {
    if (isTotalRow) {
      return WORKBOOK_COLORS.totalRowBackground;
    }

    if (isEvenRow) {
      return isEmphasized
        ? WORKBOOK_COLORS.emphasizedEvenRowBackground
        : WORKBOOK_COLORS.evenRowBackground;
    }

    return isEmphasized && !isHeader
      ? WORKBOOK_COLORS.emphasizedOddRowBackground
      : WORKBOOK_COLORS.defaultBackground;
  }
}
